import React, {
  ReactElement,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react'
import SvgPencilIcon from 'src/components/Svgs/SvgPencilIcon'
import { useTranslate } from 'src/localization/Trns'

import * as summaryStyle from '../CardSummary/CardSummary.style'
import * as style from './CardNickname.style'

type CardNicknameProps = {
  nickname: string
  /**
   * Whether this user may rename the card — the `canUpdateCreditCard` permission
   * the page already resolves, threaded down. Required rather than defaulted so
   * a new caller cannot forget it and silently expose the editor; the page
   * passes `=== true`, so a still-loading permission reads as false and the name
   * renders read-only until the answer arrives.
   */
  canRename: boolean
  /**
   * Persists the new name. `onSuccess` is a `mutate(...)`-level callback, so it
   * is UI-only (closing the editor) — the cache invalidation that keeps the
   * header in sync lives on the mutation itself.
   */
  onRename: (nickname: string, options?: { onSuccess?: () => void }) => void
  isSaving: boolean
  isError: boolean
  /** Pencil glyph size in px. Defaults to the size the full hero has always used. */
  pencilSize?: number
}

/**
 * The card name, and its inline editor.
 *
 * Without `canRename` the name is plain text: `PUT /creditcards/{id}` is gated
 * on the same permission server-side, so an ungated pencil would hand every
 * reader a control whose only outcome is a server error.
 *
 * Read mode is a plain `<button>` (never a `div onClick`) whose accessible name
 * contains the visible label, per WCAG 2.5.3 Label in Name — a speech-input user
 * can only say the text they can see.
 *
 * Edit mode is a real `<form>`: Enter submits, Escape cancels, and Save/Cancel
 * are visible controls rather than relying on blur, which is invisible to
 * keyboard users and silently discards work. The input keeps a visually-hidden
 * `<label>` rather than a bare `aria-label` so it is labelled the same way for
 * every assistive technology.
 */
const CardNickname: React.FC<CardNicknameProps> = ({
  nickname,
  canRename,
  onRename,
  isSaving,
  isError,
  pencilSize = 20,
}): ReactElement => {
  const translate = useTranslate()
  const inputId = useId()
  // The rename error is a real validation message for the field, so it is wired
  // to the input with aria-describedby (and aria-invalid) rather than only being
  // announced once by role="alert" — a user who tabs back to the field later
  // must still be told why it did not save.
  const errorId = `${inputId}-error`

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(nickname)

  // Focus follows the control the user just activated, so a keyboard user is not
  // dropped at the top of the document when the editor opens. A *stable*
  // callback ref rather than `autoFocus` (banned) or an effect: React invokes it
  // once on mount and once with null on unmount, so it cannot steal focus back
  // on a later re-render the way an inline arrow ref would.
  const focusOnMount = useCallback((node: HTMLInputElement | null): void => {
    node?.focus()
  }, [])

  // Closing the editor unmounts the focused input, which would otherwise drop a
  // keyboard user back to the top of the document (WCAG 2.4.3). Hand focus back
  // to the trigger that opened it, using the same stable-callback-ref trick so
  // the initial page render never steals focus.
  const returningFromEdit = useRef(false)
  const focusTriggerOnReturn = useCallback(
    (node: HTMLButtonElement | null): void => {
      if (node !== null && returningFromEdit.current) {
        returningFromEdit.current = false
        node.focus()
      }
    },
    [],
  )

  const startEditing = (): void => {
    setDraft(nickname)
    setEditing(true)
  }

  const closeEditor = (): void => {
    returningFromEdit.current = true
    setEditing(false)
  }

  const submit = (event: React.FormEvent): void => {
    event.preventDefault()
    const trimmed = draft.trim()
    // An empty name is not a rename; close without persisting a blank card name.
    if (trimmed.length === 0 || trimmed === nickname) {
      closeEditor()
      return
    }
    onRename(trimmed, { onSuccess: closeEditor })
  }

  // Checked ahead of the `editing` branch, not only in place of the trigger, so
  // a permission that resolves to false while the editor is open collapses it
  // rather than leaving a live form the server will reject on submit.
  if (!canRename) {
    return (
      <span css={style.readOnlyName} data-testid="card-nickname-readonly">
        {nickname}
      </span>
    )
  }

  if (!editing) {
    return (
      <>
        <button
          type="button"
          // WCAG 2.5.3 (Label in Name): the accessible name must contain the
          // visible label, or speech-input users cannot target the control by
          // the only text they can see.
          aria-label={translate('${nickname}, edit card nickname', {
            nickname,
          })}
          css={summaryStyle.nicknameTrigger}
          ref={focusTriggerOnReturn}
          onClick={startEditing}
          data-testid="card-nickname-trigger"
        >
          {nickname}
          <SvgPencilIcon
            width={pencilSize}
            height={pencilSize}
            fill="currentColor"
            stroke="currentColor"
          />
        </button>
        {isError && (
          <span
            id={errorId}
            css={style.error}
            role="alert"
            data-testid="card-nickname-error"
          >
            {translate('Card name could not be saved. Try again.')}
          </span>
        )}
      </>
    )
  }

  return (
    <form
      css={style.editForm}
      onSubmit={submit}
      data-testid="card-nickname-form"
    >
      <label htmlFor={inputId} css={style.visuallyHidden}>
        {translate('Card name')}
      </label>
      <input
        id={inputId}
        ref={focusOnMount}
        css={style.input}
        value={draft}
        disabled={isSaving}
        aria-invalid={isError}
        aria-describedby={isError ? errorId : undefined}
        onChange={(event) => setDraft(event.target.value)}
        // Escape abandons the edit. Bound on the input (an interactive element)
        // rather than the form, so it is a real control's own key handler.
        onKeyDown={(event) => {
          if (event.key === 'Escape') closeEditor()
        }}
        data-testid="card-nickname-input"
      />
      <button
        type="submit"
        css={style.action}
        disabled={isSaving}
        aria-busy={isSaving}
        data-testid="card-nickname-save"
      >
        {isSaving ? translate('Saving') : translate('Save')}
      </button>
      <button
        type="button"
        css={style.action}
        disabled={isSaving}
        onClick={closeEditor}
        data-testid="card-nickname-cancel"
      >
        {translate('Cancel')}
      </button>
      {isError && (
        <span
          id={errorId}
          css={style.error}
          role="alert"
          data-testid="card-nickname-error"
        >
          {translate('Card name could not be saved. Try again.')}
        </span>
      )}
    </form>
  )
}

export default CardNickname
